// THE UPLOAD DUPLICATE RULE — PURE, shared by the server (authoritative) and the uploader (courtesy).
//
// Scott's rule (2026-10-12): FILENAME AND SIZE TOGETHER. "Sometimes the names can be the same but both Name &
// Size at the same time would constitute a duplicate." Checked against LIVE assets in the SAME club.
//
// ⚠️ `md5` is null until the worker has processed the file, so it does not exist at plan time.
// `originalFilename` + `sizeBytes` is the only pair that does, and the declared size is trustworthy because
// `completeUpload` refuses an object of any other size.
// ⚠️ The filename comparison is EXACT (as the browser reported it): Firestore's `in` is case-sensitive, and the
// same file re-selected from the same folder keeps its spelling.
// ⚠️ A REJECTED asset is live (deletedAt == null), so rejected assets block a re-upload. That follows from the
// rule as chosen, and is flagged for Scott in the handback.

export type DuplicateCandidate = { filename: string; sizeBytes: number };

/** The one sentence the uploader shows when the server's duplicate check did not run. */
export const DUPLICATE_CHECK_NOT_RUN_NOTICE =
  'We couldn\'t check these files against what\'s already in this club\'s assets, so a file that was uploaded before may have been added again.';

/** ⚠️ Anything but an explicit `true` counts as "did not run" — an older server that never says so must not read as a pass. */
export function duplicateCheckNotice(response: { duplicateCheckRan?: boolean }): string | null {
  return response.duplicateCheckRan === true ? null : DUPLICATE_CHECK_NOT_RUN_NOTICE;
}

export type DuplicateSkip = { index: number; filename: string; reason: string };

export function duplicateKey(c: DuplicateCandidate): string {
  return `${c.sizeBytes}\u0000${c.filename}`;
}

export function inLibraryReason(filename: string): string {
  return `“${filename}” is already in this club's assets, so it was skipped.`;
}

export function inBatchReason(filename: string): string {
  return `“${filename}” was selected more than once, so it was only uploaded once.`;
}

/**
 * Which of `files` to skip. `existing` = the club's live assets (server) or [] (the uploader's courtesy check).
 * ⚠️ Within the batch the FIRST occurrence is kept: the same file selected twice never reaches Firestore to be
 * compared against itself.
 */
export function planDuplicateSkips(
  files: readonly DuplicateCandidate[],
  existing: readonly DuplicateCandidate[],
): DuplicateSkip[] {
  const stored = new Set(existing.map(duplicateKey));
  const seen = new Set<string>();
  const skips: DuplicateSkip[] = [];
  files.forEach((f, index) => {
    const key = duplicateKey(f);
    if (stored.has(key)) skips.push({ index, filename: f.filename, reason: inLibraryReason(f.filename) });
    else if (seen.has(key)) skips.push({ index, filename: f.filename, reason: inBatchReason(f.filename) });
    else seen.add(key);
  });
  return skips;
}
